import { JobStatusBadge } from '@/components/jobs/JobStatusBadge'
import { ErrorDetails } from '@/components/ui/Alert'
import { Card, CardHeader } from '@/components/ui/Card'
import { EmptyState } from '@/components/ui/EmptyState'
import { Skeleton } from '@/components/ui/Spinner'
import { cn } from '@/lib/cn'
import { presentJobError } from '@/lib/errors'
import { formatDuration, formatNumber, formatRelative } from '@/lib/format'
import type { Job } from '@/types/api'

const JOB_TYPE_LABELS: Record<Job['jobType'], string> = {
  json_import: 'File import',
  curl_capture: 'cURL capture',
  browser_search: 'Live search',
}

const ClockIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
    <path
      d="M12 7.5V12l3 2"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-1.5">
      <dt className="text-xs text-ink-muted">{label}</dt>
      <dd className="tabular text-sm font-medium text-ink">{value}</dd>
    </div>
  )
}

/**
 * Is this progress message just the error again?
 *
 * The engine logs the failure as a message *and* stores it as the job's error,
 * usually prefixed with the search it came from. Rendering both puts the same
 * sentence on screen twice, the second time in the raw form the error box
 * exists to avoid.
 */
function echoesError(message: string, error: string): boolean {
  if (!error) return false
  const strip = (text: string) =>
    text
      .replace(/^[^:]{0,40}:\s*/, '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim()
  const body = strip(message)
  if (body.length < 20) return false
  const haystack = strip(error)
  return haystack.includes(body) || body.includes(haystack)
}

function JobRow({ job }: { job: Job }) {
  // A failed job's stored error is one long assembled line. Show the sentence
  // that says what happened and keep the rest behind a disclosure, rather than
  // dropping the whole thing on the page in red.
  const failure = job.error ? presentJobError(job.error) : null
  const messages = job.messages.filter((message) => !echoesError(message, job.error))

  return (
    <li className="border-b border-line-subtle px-4 py-3 last:border-b-0">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
        <div className="flex min-w-0 items-center gap-2">
          <JobStatusBadge status={job.status} />
          <span className="truncate text-base font-medium text-ink">{job.label}</span>
        </div>
        <span className="shrink-0 text-xs text-ink-muted">
          {JOB_TYPE_LABELS[job.jobType]} · {formatRelative(job.createdAt)}
        </span>
      </div>

      <dl className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
        <Metric label="New" value={formatNumber(job.recordsCreated)} />
        <Metric label="Merged" value={formatNumber(job.recordsMerged)} />
        <Metric label="Seen" value={formatNumber(job.recordsIngested)} />
        {job.durationSeconds !== null ? (
          <Metric label="Took" value={formatDuration(job.durationSeconds)} />
        ) : null}
        {job.requestsAllowed > 0 || job.requestsBlocked > 0 ? (
          <Metric
            label="Guard"
            value={`${formatNumber(job.requestsAllowed)} allowed, ${formatNumber(job.requestsBlocked)} blocked`}
          />
        ) : null}
      </dl>

      {failure ? (
        <div className="mt-2.5 rounded-md border border-critical/30 bg-critical-soft px-3 py-2">
          <p className="text-sm font-medium text-ink">{failure.title}</p>
          <p className="mt-0.5 text-xs text-ink-secondary">{failure.message}</p>
          {failure.detail && failure.detail !== failure.message ? (
            <ErrorDetails text={failure.detail} />
          ) : null}
        </div>
      ) : null}

      {messages.length > 0 ? (
        <ul className="mt-2 flex flex-col gap-1">
          {messages.slice(0, 4).map((message, index) => (
            <li
              key={index}
              className="flex gap-1.5 text-xs break-words text-ink-muted"
            >
              <span aria-hidden="true" className="text-ink-faint">
                ·
              </span>
              {message}
            </li>
          ))}
          {messages.length > 4 ? (
            <li className="text-xs text-ink-muted">+{messages.length - 4} more</li>
          ) : null}
        </ul>
      ) : null}
    </li>
  )
}

export interface JobListProps {
  jobs: Job[]
  loading?: boolean
  /**
   * Height of the whole card. The run list scrolls inside it rather than
   * extending the page, so the panel stays put while the main column scrolls.
   *
   * The desktop value leaves ~3rem of slack below the viewport. A sticky
   * element cannot leave its own grid row, so at the very bottom of the page
   * it gets pushed up by however much it overhangs the row's end; the slack is
   * what keeps its header from tucking under the app bar there.
   */
  maxHeightClassName?: string
}

export function JobList({
  jobs,
  loading,
  maxHeightClassName = 'max-h-[32rem] xl:max-h-[calc(100vh-11rem)]',
}: JobListProps) {
  const running = jobs.filter(
    (job) => job.status === 'running' || job.status === 'pending',
  ).length

  return (
    <Card className={cn('flex flex-col overflow-hidden', maxHeightClassName)}>
      <CardHeader
        className="shrink-0"
        title="Activity"
        description={
          jobs.length > 0
            ? `${formatNumber(jobs.length)} run${jobs.length === 1 ? '' : 's'}, newest first.`
            : 'Every ingestion run, newest first.'
        }
        meta={
          running > 0 ? (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-accent-ring/45 bg-accent-soft px-2 py-0.5 text-2xs font-medium text-accent-text">
              <svg viewBox="0 0 12 12" className="h-2.5 w-2.5 animate-spin" aria-hidden="true">
                <circle
                  cx="6"
                  cy="6"
                  r="4.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  strokeDasharray="21"
                  strokeDashoffset="14"
                />
              </svg>
              {running} in flight
            </span>
          ) : null
        }
      />
      {loading && jobs.length === 0 ? (
        <div className="flex flex-col gap-3 p-4">
          {Array.from({ length: 3 }, (_, index) => (
            <Skeleton key={index} className="h-16 w-full" />
          ))}
        </div>
      ) : jobs.length === 0 ? (
        <EmptyState
          size="compact"
          icon={<ClockIcon />}
          title="Nothing has run yet"
          description="Start an import or a live search to bring judgment records into the database."
        />
      ) : (
        <ul className="scrollbar-slim min-h-0 flex-1 overflow-y-auto overscroll-contain">
          {jobs.map((job) => (
            <JobRow key={job.id} job={job} />
          ))}
        </ul>
      )}
    </Card>
  )
}
